import React, { useEffect, useState } from "react";
import { Formik, Form, Field } from "formik";
import ReactPaginate from "react-paginate";
import * as Yup from "yup";
import Modal from "react-modal";
import { useDispatch, useSelector } from "react-redux";
import { listNotes, deleteNote, addNote, sendNoteEmail } from "../../store/reducer/notes/noteSlice";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faSpinner,
  faAngleRight,
  faAngleLeft,
  faEllipsis,
  faNoteSticky,
  faTimes,
  faPaperPlane
} from "@fortawesome/free-solid-svg-icons";
import { toast } from "react-toastify";

Modal.setAppElement("#root");

function formatDate(isoDateString) {
  if (!isoDateString) return "";
  const date = new Date(isoDateString);
  const day = String(date.getDate()).padStart(2, "0");
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const year = date.getFullYear();
  const hours = String(date.getHours()).padStart(2, "0");
  const minutes = String(date.getMinutes()).padStart(2, "0");
  return `${day}-${month}-${year} ${hours}:${minutes}`;
}

/**
 * NotesTable with modal Add-Note + delete + pagination,
 * and a wide, well-wrapping Note column.
 *
 * Props:
 * - drawing_id: string | number (required)
 * - type: string (required) -> e.g., "edit-drawing"
 * - limit?: number (default 10)
 */
function NotesTable({ drawing_id, type, limit: defaultLimit = 10 }) {
  const dispatch = useDispatch();
  const { loading, notes = [], totalCount = 0 } = useSelector(
    (state) => state.notes
  );

  const [page, setPage] = useState(1);
  const [limit] = useState(defaultLimit);

  // modal state
  const [isModalOpen, setIsModalOpen] = useState(false);

  // validation and initial values for add-note form
  const addNoteSchema = Yup.object().shape({
    note_text: Yup.string().trim().required("Please enter note text."),
  });
  const addNoteInitialValues = { note_text: "" };

  const [isSendOpen, setIsSendOpen] = useState(false);
  const [sendTarget, setSendTarget] = useState(null); // note object

  const openSend = (note) => { setSendTarget(note); setIsSendOpen(true); };
  const closeSend = () => { setSendTarget(null); setIsSendOpen(false); };

  const sendSchema = Yup.object().shape({
    toEmail: Yup.string().email("Invalid email").required("Email is required"),
  });

  useEffect(() => {
    if (!drawing_id || !type) return;
    dispatch(listNotes({ drawing_id, type, page, limit }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [drawing_id, type, page, limit, dispatch]);

  const handlePageClick = ({ selected }) => {
    setPage(selected + 1);
  };

  const handleDelete = async (id) => {
    const res = await dispatch(deleteNote(id));
    if (res?.payload) {
      toast.success(res.payload.message);
      dispatch(listNotes({ drawing_id, type, page, limit }));
    } else {
      toast.error(res?.error?.message || "Failed to delete note");
    }
  };

  const handleAddNote = async (values, { setSubmitting, resetForm }) => {
    try {
      setSubmitting(true);
      const createdBy = JSON.parse(localStorage.getItem("userSession"))?.id;
      const formData = new FormData();
      formData.append("note_text", values.note_text);
      formData.append("createdBy", createdBy);
      formData.append("drawing_id", drawing_id);
      formData.append("type", type);

      const res = await dispatch(addNote(formData));
      if (res?.payload) {
        toast.success(res.payload.message);
        resetForm();
        setIsModalOpen(false);
        // Refresh current page
        dispatch(listNotes({ drawing_id, type, page, limit }));
      } else {
        toast.error(res?.error?.message || "Failed to add note");
      }
    } finally {
      setSubmitting(false);
    }
  };

  const pageCount = Math.max(1, Math.ceil((totalCount || 0) / limit));

  return (
    <div className="mt-6">
      <div className="md:p-2 bg-[#F8F8F8] rounded-[12px]">
        <div className="flex flex-col">
          <div className="md:flex justify-between items-center p-[12px]">
            <div className="leading-[30px] font-Mulish font-[700] text-[20px] mb-[8px] md:mb-0">
              <span>Notes</span>
            </div>
            <button
              onClick={() => setIsModalOpen(true)}
              className="flex items-center gap-2 py-2 px-3 rounded-full hover:bg-gray-100 transition-colors duration-200 bg-gray-200"
              title="Add Note"
            >
              <FontAwesomeIcon icon={faNoteSticky} className="text-gray-700" />
              <span className="font-Inter text-[14px]">Add Note</span>
            </button>
          </div>

          {/* Notes Table */}
          <div className="sm:rounded-lg mt-3 overflow-auto">
            <table className="w-full table-fixed text-center">
              {/* Wide Note column */}
              <colgroup>
                <col className="w-[60%]" />
                <col className="w-[14%]" />
                <col className="w-[16%]" />
                <col className="w-[10%]" />
              </colgroup>

              <thead className="bg-white">
                <tr className="rounded-[12px]">
                  <th className="px-6 py-3 font-[Inter] font-[600] text-[14px] text-[#6D6D6F] leading-[20px] tracking-wider text-left">
                    Note
                  </th>
                  <th className="px-3 py-3 font-[Inter] font-[600] text-[14px] text-[#6D6D6F] leading-[20px] tracking-wider">
                    Created By
                  </th>
                  <th className="px-3 py-3 font-[Inter] font-[600] text-[14px] text-[#6D6D6F] leading-[20px] tracking-wider">
                    Created On
                  </th>
                  <th className="px-3 py-3 font-[Inter] font-[600] text-[14px] text-[#6D6D6F] leading-[20px] tracking-wider">
                    Options
                  </th>
                </tr>
              </thead>

              {loading ? (
                <tbody>
                  <tr>
                    <td colSpan="4" className="py-6">
                      <FontAwesomeIcon icon={faSpinner} size="xl" spin />
                    </td>
                  </tr>
                </tbody>
              ) : (
                <tbody>
                  {notes?.length > 0 ? (
                    notes.map((note) => (
                      <tr key={note.id}>
                        <td className="px-6 py-4 text-[14px] text-[#3A3A3C] text-left align-top whitespace-pre-wrap break-words leading-6">
                          {note.note_text}
                        </td>
                        <td
                          className="px-3 py-4 text-[14px] text-[#6D6D6F] align-top whitespace-nowrap overflow-hidden text-ellipsis truncate"
                          title={note.createdBy}
                        >
                          {note.createdBy}
                        </td>
                        <td
                          className="px-3 py-4 text-[14px] text-[#6D6D6F] align-top whitespace-nowrap"
                          title={formatDate(note.createdAt)}
                        >
                          {formatDate(note.createdAt)}
                        </td>
                        <td className="px-3 py-2 align-top">
                          <div className="flex gap-[10px] justify-center">
                            <button
                              type="button"
                              onClick={() => handleDelete(note.id)}
                              className="rounded-lg bg-red-100 hover:bg-red-200 transition p-2"
                              title="Delete"
                            >
                              <img
                                src={process.env.PUBLIC_URL + "/icons/delete.png"}
                                alt="Delete"
                                className="w-[32px]"
                              />
                            </button>
                            <button
                              type="button"
                              onClick={() => openSend(note)}
                              className="rounded-lg bg-blue-100 hover:bg-blue-200 transition p-2"
                              title="Send note via email"
                            >
                              <FontAwesomeIcon icon={faPaperPlane} className="w-[18px] h-[18px]" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="4" className="text-center py-4 text-gray-500">
                        No notes found!
                      </td>
                    </tr>
                  )}
                </tbody>
              )}
            </table>
          </div>
        </div>
      </div>

      {/* Pagination Footer */}
      <div className="flex justify-between items-center mt-[10px] gap-[24px] md:p-2 bg-[#F8F8F8]">
        <div className="hidden md:block font-Inter font-[300] text-nowrap text-[12px]">
          <span>
            Showing <b>{page}</b> of <b>{pageCount}</b> pages
          </span>
        </div>

        <div className="w-full flex justify-end">
          <ReactPaginate
            className="react_pagination_ul"
            marginPagesDisplayed={1}
            pageRangeDisplayed={2}
            breakLabel={
              <span className="w-8 h-8 flex justify-center items-center bg-white rounded-md mr-2 text-[12px]">
                <FontAwesomeIcon icon={faEllipsis} size="sm" />
              </span>
            }
            previousLabel={
              <span className="w-8 h-8 flex justify-center items-center bg-white rounded-md mr-2 text-[12px]">
                <FontAwesomeIcon icon={faAngleLeft} size="sm" />
              </span>
            }
            nextLabel={
              <span className="w-8 h-8 flex justify-center items-center bg-white rounded-md text-[12px]">
                <FontAwesomeIcon icon={faAngleRight} size="sm" />
              </span>
            }
            onPageChange={handlePageClick}
            pageCount={pageCount}
            // li (page) styling
            pageClassName="block hover:bg-black hover:text-white rounded-md mr-2 text-[12px]"
            // anchor inside li
            pageLinkClassName="w-8 h-8 flex justify-center items-center"
            // active styles
            activeClassName="bg-black text-white"
            activeLinkClassName="text-white"
            // container
            containerClassName="flex items-center justify-center"
            renderOnZeroPageCount={null}
          />
        </div>
      </div>


      {/* Add Note Modal */}
      <Modal
        isOpen={isModalOpen}
        onRequestClose={() => setIsModalOpen(false)}
        contentLabel="Add Note"
        className="bg-white rounded-[12px] p-6 w-[90%] max-w-[700px] max-h-[80vh] mx-auto border border-gray-300 relative overflow-auto focus:outline-none"
        overlayClassName="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center"
        shouldCloseOnOverlayClick
        shouldCloseOnEsc
      >
        <button
          className="absolute top-3 right-3 text-gray-500 hover:text-gray-700 focus:outline-none"
          onClick={() => setIsModalOpen(false)}
          aria-label="Close"
        >
          <FontAwesomeIcon icon={faTimes} size="lg" />
        </button>
        <h2 className="font-Inter font-[700] text-[22px] mb-4 text-center">
          Add Note
        </h2>

        <Formik
          initialValues={addNoteInitialValues}
          validationSchema={addNoteSchema}
          onSubmit={handleAddNote}
        >
          {({ isSubmitting, errors, touched, setFieldValue }) => (
            <Form>
              <label
                htmlFor="note_text"
                className="font-Inter font-[400] text-[15px] text-[#9E9E9E]"
              >
                Note
              </label>
              <Field
                as="textarea"
                id="note_text"
                name="note_text"
                rows={6}
                className="mt-1 block w-full bg-white border border-gray-200 text-gray-700 py-2 px-3 rounded-[8px] leading-6 focus:outline-none focus:bg-white focus:border-gray-500"
                onChange={(e) => setFieldValue("note_text", e.target.value)}
                placeholder="Type your note here…"
              />
              {errors.note_text && touched.note_text ? (
                <div className="text-red-700 mt-1">{errors.note_text}</div>
              ) : null}

              <div className="flex justify-end gap-2 mt-5">
                <button
                  type="button"
                  onClick={() => setIsModalOpen(false)}
                  className="py-[10px] px-[18px] rounded-[8px] bg-gray-200 hover:bg-gray-300 text-[14px]"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className={`py-[10px] px-[18px] rounded-[8px] text-white text-[14px] ${
                    isSubmitting ? "bg-gray-300" : "bg-[#1C1C20]"
                  }`}
                  disabled={isSubmitting}
                >
                  {isSubmitting ? <FontAwesomeIcon icon={faSpinner} spin /> : "Save"}
                </button>
              </div>
            </Form>
          )}
        </Formik>
      </Modal>

      <Modal
        isOpen={isSendOpen}
        onRequestClose={closeSend}
        contentLabel="Send Note"
        className="bg-white rounded-[12px] p-6 w-[90%] max-w-[560px] mx-auto border border-gray-300 relative overflow-auto focus:outline-none"
        overlayClassName="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center"
        >
        <button
          className="absolute top-3 right-3 text-gray-500 hover:text-gray-700"
          onClick={closeSend}
          aria-label="Close"
        >
          <FontAwesomeIcon icon={faTimes} size="lg" />
        </button>

        <h2 className="font-Inter font-[700] text-[20px] mb-4 text-center">
          Send Note via Email
        </h2>

        <Formik
          initialValues={{ toEmail: "" }}
          validationSchema={sendSchema}
          onSubmit={async (values, { setSubmitting, resetForm }) => {
            try {
              const res = await dispatch(
                sendNoteEmail({ noteId: sendTarget.id, toEmail: values.toEmail })
              );
              if (res?.payload) {
                toast.success(res.payload.message || "Email sent");
                resetForm();
                closeSend();
              } else {
                toast.error(res?.error?.message || "Failed to send email");
              }
            } finally {
              setSubmitting(false);
            }
          }}
        >
          {({ isSubmitting, errors, touched }) => (
            <Form>
              <label className="font-Inter text-[14px] text-[#6D6D6F]" htmlFor="toEmail">
                Recipient Email
              </label>
              <Field
                id="toEmail"
                name="toEmail"
                type="email"
                placeholder="name@example.com"
                className="mt-1 block w-full bg-white border border-gray-200 text-gray-700 py-2 px-3 rounded-[8px] focus:outline-none"
              />
              {errors.toEmail && touched.toEmail && (
                <div className="text-red-700 mt-1">{errors.toEmail}</div>
              )}

              <div className="mt-5 flex justify-end gap-2">
                <button
                  type="button"
                  onClick={closeSend}
                  className="py-[10px] px-[18px] rounded-[8px] bg-gray-200 hover:bg-gray-300 text-[14px]"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className={`py-[10px] px-[18px] rounded-[8px] text-white text-[14px] ${
                    isSubmitting ? "bg-gray-300" : "bg-[#1C1C20]"
                  }`}
                >
                  {isSubmitting ? <FontAwesomeIcon icon={faSpinner} spin /> : "Send"}
                </button>
              </div>

              {/* Optional preview of what will be sent */}
              {sendTarget && (
                <div className="mt-4 p-3 rounded bg-gray-50 text-sm text-gray-700">
                  <div><b>Note:</b> {sendTarget.note_text}</div>
                  <div><b>By:</b> {sendTarget.createdBy}</div>
                  <div><b>On:</b> {formatDate(sendTarget.createdAt)}</div>
                </div>
              )}
            </Form>
          )}
        </Formik>
        </Modal>

    </div>
  );
}

export default NotesTable;
